import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  Sparkles, ChevronRight, ChevronLeft, Loader2, Rocket, FileText, Palette,
  Users, ClipboardList, Wallet, Wand2, Building2, Trash2, CheckCircle2,
  Download, Presentation as PresentIcon, Mail as MailIcon, Link as LinkIcon,
} from "lucide-react";
import { getClients } from "../lib/api";
import ProposalDeckViewer from "../components/ProposalDeckViewer";

const API = `${API_BASE}/api`;

/**
 * ProspectProposalBuilderPage — 4-step guided wizard.
 *
 *   1. Client → pick existing or type a new one
 *   2. Business details → industry / area / website / budget / target customer
 *   3. Recommend → 6-question checkbox questionnaire, contract term, discount
 *   4. Preview & generate → editable line items, generate → poll → download/email
 *
 * Reps like guardrails so we keep the wizard linear. All state is in memory
 * until step 4 kicks off the async Claude + Nano Banana pipeline.
 */
export default function ProspectProposalBuilderPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [config, setConfig] = useState({ catalog: [], questions: [] });
  const [clients, setClients] = useState([]);
  const [existing, setExisting] = useState([]);

  // Wizard state
  const [clientId, setClientId] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [industry, setIndustry] = useState("");
  const [serviceArea, setServiceArea] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [keyServices, setKeyServices] = useState("");
  const [targetCustomer, setTargetCustomer] = useState("");
  const [monthlyAdBudget, setMonthlyAdBudget] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [flags, setFlags] = useState({});
  const [contractMonths, setContractMonths] = useState(12);
  const [discountPct, setDiscountPct] = useState(0);

  const [recommend, setRecommend] = useState({ bundle_keys: [], line_items: [] });
  const [creating, setCreating] = useState(false);
  const [propId, setPropId] = useState(null);
  const [propStatus, setPropStatus] = useState(null);
  const [initialProposal, setInitialProposal] = useState(null);

  useEffect(() => {
    axios.get(`${API}/proposal-builder/catalog`)
      .then(r => setConfig(r.data))
      .catch(() => toast.error("Couldn't load product catalog"));
    getClients().then(r => setClients(r.data || [])).catch(() => {});
    axios.get(`${API}/prospect-proposals`)
      .then(r => setExisting(r.data || []))
      .catch(() => {});
  }, []);

  // Auto-recommend as flags change
  useEffect(() => {
    if (!Object.values(flags).some(Boolean)) {
      setRecommend({ bundle_keys: [], line_items: [] });
      return;
    }
    axios.post(`${API}/proposal-builder/recommend`, { flags })
      .then(r => setRecommend(r.data))
      .catch(() => {});
  }, [flags]);

  // When rep picks an existing client, auto-fill business name + website
  useEffect(() => {
    if (!clientId) return;
    const c = clients.find(x => x.id === clientId);
    if (!c) return;
    if (!businessName) setBusinessName(c.company_name || c.name || "");
    if (!websiteUrl && c.website_url) setWebsiteUrl(c.website_url);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, clients]);

  const step1Valid = businessName.trim().length >= 2;
  const step3Valid = Object.values(flags).some(Boolean);

  const goNext = () => {
    if (step === 1 && !step1Valid) { toast.error("Business name is required"); return; }
    if (step === 3 && !step3Valid) { toast.error("Pick at least one questionnaire item so we know what to recommend"); return; }
    setStep(s => Math.min(4, s + 1));
  };
  const goBack = () => setStep(s => Math.max(1, s - 1));

  const kickoffProposal = async () => {
    setCreating(true);
    try {
      const payload = {
        client_id: clientId || null,
        business_name: businessName.trim(),
        industry: industry.trim() || null,
        service_area: serviceArea.trim() || null,
        website_url: websiteUrl.trim() || null,
        key_services: keyServices.trim() || null,
        target_customer: targetCustomer.trim() || null,
        monthly_ad_budget: monthlyAdBudget ? Number(monthlyAdBudget) : null,
        additional_notes: additionalNotes.trim() || null,
        flags,
        contract_months: contractMonths,
        discount_pct: discountPct,
        line_items: recommend.line_items,
      };
      const r = await axios.post(`${API}/prospect-proposals`, payload);
      // Show the status view immediately with whatever the POST returned so
      // the user sees "Generating…" without waiting for the first GET.
      setInitialProposal(r.data);
      setPropId(r.data.id);
      setPropStatus(r.data.status);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start proposal generation");
      setCreating(false);
    }
  };

  // Poll the proposal until ready
  useEffect(() => {
    if (!propId || propStatus === "ready" || propStatus === "failed") return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/prospect-proposals/${propId}`);
        setPropStatus(r.data.status);
        if (r.data.status === "ready") toast.success("Proposal ready!");
        if (r.data.status === "failed") toast.error("Generation failed — try again");
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [propId, propStatus]);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-fuchsia-600 text-xs font-bold tracking-widest uppercase">
            <Sparkles className="h-4 w-4" /> Prospect IQ Proposal Builder
          </div>
          <h1 className="font-outfit text-3xl font-bold text-slate-900 mt-1">Build a custom proposal</h1>
          <p className="text-sm text-slate-600 mt-1">
            A guided wizard that turns a short intake into a full pitch deck — demographics, purchase triggers, sample creative, and pricing.
          </p>
        </div>
      </div>

      {/* Progress */}
      {!propId && (
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-5 pb-4">
            <Progress value={step * 25} className="h-2" />
            <div className="grid grid-cols-4 gap-2 mt-3 text-xs">
              {[
                { n: 1, label: "Client", icon: Users },
                { n: 2, label: "Business", icon: Building2 },
                { n: 3, label: "Recommend", icon: ClipboardList },
                { n: 4, label: "Preview & Generate", icon: Rocket },
              ].map(s => {
                const Icon = s.icon;
                return (
                  <div key={s.n}
                    data-testid={`wizard-step-${s.n}`}
                    className={`flex items-center gap-2 rounded-md px-3 py-2 border ${step >= s.n ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200"}`}>
                    <Icon className="h-3.5 w-3.5" />
                    <span className="font-semibold">Step {s.n}</span>
                    <span className="opacity-80">· {s.label}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 1 — Client */}
      {!propId && step === 1 && (
        <Card className="border-0 shadow-lg" data-testid="wizard-panel-step-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-fuchsia-600" /> Who is this proposal for?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Link to an existing client (optional)</Label>
              <Select value={clientId || "__new"} onValueChange={(v) => setClientId(v === "__new" ? "" : v)}>
                <SelectTrigger data-testid="wizard-client-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__new">— New prospect (not in client list yet) —</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.company_name || c.name} · {c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">Linking auto-fills business name + website and shows the proposal on the client&apos;s page.</p>
            </div>
            <div>
              <Label htmlFor="biz">Business Name <span className="text-red-500">*</span></Label>
              <Input id="biz" data-testid="wizard-business-name-input" value={businessName} onChange={e => setBusinessName(e.target.value)} placeholder="Cascade Auto & Marine" className="mt-1" />
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 2 — Business Details */}
      {!propId && step === 2 && (
        <Card className="border-0 shadow-lg" data-testid="wizard-panel-step-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-fuchsia-600" /> Tell us about their business</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Industry / Trades</Label>
                <Input data-testid="wizard-industry-input" value={industry} onChange={e => setIndustry(e.target.value)} placeholder="Auto Repair, Marine Repair" className="mt-1" />
              </div>
              <div>
                <Label>Service Area</Label>
                <Input data-testid="wizard-service-area-input" value={serviceArea} onChange={e => setServiceArea(e.target.value)} placeholder="Pacific Northwest" className="mt-1" />
              </div>
              <div>
                <Label>Website URL</Label>
                <Input data-testid="wizard-website-input" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://theirsite.com" className="mt-1" />
              </div>
              <div>
                <Label>Monthly Ad Budget (optional)</Label>
                <Input data-testid="wizard-budget-input" type="number" value={monthlyAdBudget} onChange={e => setMonthlyAdBudget(e.target.value)} placeholder="1500" className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Key Services to Highlight</Label>
              <Input data-testid="wizard-services-input" value={keyServices} onChange={e => setKeyServices(e.target.value)} placeholder="Auto Repair, Marine Repair, Diesel Service" className="mt-1" />
            </div>
            <div>
              <Label>Target Customer Description (optional)</Label>
              <Textarea data-testid="wizard-target-customer-input" value={targetCustomer} onChange={e => setTargetCustomer(e.target.value)}
                placeholder="Boat owners and homeowners in the PNW who value fast, honest service." rows={2} className="mt-1" />
              <p className="text-xs text-slate-500 mt-1">Whatever detail you give here goes straight into Claude&apos;s demographics slide.</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 3 — Questionnaire */}
      {!propId && step === 3 && (
        <Card className="border-0 shadow-lg" data-testid="wizard-panel-step-3">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5 text-fuchsia-600" /> What do they need?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">Check any that apply. We&apos;ll bundle the right products.</p>
            <div className="space-y-2">
              {config.questions.map(q => (
                <label key={q.flag} className={`flex items-start gap-3 border rounded-lg p-3 cursor-pointer ${flags[q.flag] ? "border-fuchsia-500 bg-fuchsia-50" : "border-slate-200 hover:border-slate-300"}`}>
                  <Checkbox
                    data-testid={`wizard-flag-${q.flag}`}
                    checked={!!flags[q.flag]}
                    onCheckedChange={(v) => setFlags(f => ({ ...f, [q.flag]: !!v }))}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-900">{q.label}</div>
                    {q.is_upsell && <div className="text-xs text-slate-500 mt-0.5">Upsell — pairs with any core program</div>}
                  </div>
                </label>
              ))}
            </div>

            {/* Live recommendation preview */}
            {recommend.bundle_keys.length > 0 && (
              <div className="border rounded-lg bg-slate-50 p-4 mt-4" data-testid="wizard-live-recommendation">
                <div className="text-xs font-bold tracking-widest uppercase text-fuchsia-700 mb-2">Recommending</div>
                <div className="space-y-1">
                  {recommend.line_items.map(it => (
                    <div key={it.product_key} className="flex items-center justify-between text-sm">
                      <span className="text-slate-800">{it.product_name} <span className="text-slate-500">· {it.tier_name}</span></span>
                      <span className="font-mono text-slate-700">${(it.monthly || it.one_time).toLocaleString()}{it.monthly ? "/mo" : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2 pt-4 border-t">
              <div>
                <Label>Contract Term</Label>
                <Select value={String(contractMonths)} onValueChange={(v) => setContractMonths(Number(v))}>
                  <SelectTrigger data-testid="wizard-contract-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Month-to-Month</SelectItem>
                    <SelectItem value="6">6-Month</SelectItem>
                    <SelectItem value="12">12-Month</SelectItem>
                    <SelectItem value="24">24-Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bundle Discount % {(contractMonths !== 6 && contractMonths !== 12) && <span className="text-xs text-amber-600">(applies only to 6/12-mo terms)</span>}</Label>
                <Input data-testid="wizard-discount-input" type="number" min="0" max="40" value={discountPct} onChange={e => setDiscountPct(Number(e.target.value || 0))} className="mt-1" />
                <p className="text-xs text-slate-500 mt-1">Applied to the monthly bundle total for the length of the term.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 4 — Preview & Generate */}
      {!propId && step === 4 && (
        <Card className="border-0 shadow-lg" data-testid="wizard-panel-step-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Rocket className="h-5 w-5 text-fuchsia-600" /> Preview & generate</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Extra notes for the AI (optional)</Label>
              <Textarea data-testid="wizard-extra-notes-input" value={additionalNotes} onChange={e => setAdditionalNotes(e.target.value)}
                placeholder="Anything else Claude should know — competitor to reference, positioning angle, timeline pressure, etc."
                rows={3} className="mt-1" />
            </div>
            <div className="rounded-lg border bg-slate-50 p-4">
              <div className="text-xs font-bold tracking-widest uppercase text-slate-600 mb-2">You&apos;re generating</div>
              <div className="text-lg font-semibold text-slate-900">{businessName}</div>
              <div className="text-sm text-slate-600">{industry || "—"} · {serviceArea || "—"}</div>
              <div className="mt-3 space-y-1">
                {recommend.line_items.map(it => (
                  <div key={it.product_key} className="flex items-center justify-between text-sm">
                    <span className="text-slate-800">{it.product_name} <Badge variant="outline" className="ml-2 text-xs">{it.tier_name}</Badge></span>
                    <span className="font-mono text-slate-700">${(it.monthly || it.one_time).toLocaleString()}{it.monthly ? "/mo" : ""}</span>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200 flex items-center justify-between text-sm">
                <span className="text-slate-600">{contractMonths}-month term {discountPct > 0 ? `· ${discountPct}% discount` : ""}</span>
              </div>
            </div>
            <p className="text-xs text-slate-500">
              Generation runs Claude Sonnet 4.5 (demographics + triggers + creative) then Nano Banana for one FB ad creative.
              Usually completes in under 90 seconds.
            </p>
            <Button
              onClick={kickoffProposal}
              disabled={creating}
              data-testid="wizard-generate-btn"
              className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white h-12 w-full font-semibold"
            >
              {creating ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Kicking off…</>) : (<><Sparkles className="h-4 w-4 mr-2" /> Generate my proposal</>)}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Generation status */}
      {propId && (
        <ProposalStatusView
          propId={propId}
          status={propStatus}
          initialProposal={initialProposal}
          onDone={(fresh) => {
            setPropStatus(fresh.status);
            setCreating(false);
          }}
          onReset={() => {
            setPropId(null);
            setPropStatus(null);
            setInitialProposal(null);
            setCreating(false);
            setStep(1);
          }}
        />
      )}

      {/* Navigation */}
      {!propId && (
        <div className="flex justify-between">
          <Button variant="outline" onClick={goBack} disabled={step === 1} data-testid="wizard-back-btn">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          {step < 4 && (
            <Button onClick={goNext} data-testid="wizard-next-btn" className="bg-slate-900 hover:bg-slate-800 text-white">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      )}

      {/* Existing proposals list */}
      {!propId && existing.length > 0 && (
        <Card className="border-0 shadow-sm mt-8">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Recent proposals</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {existing.slice(0, 10).map(p => (
              <button key={p.id}
                onClick={() => { setPropId(p.id); setPropStatus(p.status); }}
                data-testid={`proposal-history-${p.id}`}
                className="w-full text-left border rounded-md px-3 py-2 hover:border-fuchsia-300 hover:bg-fuchsia-50 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">{p.business_name}</div>
                  <div className="text-xs text-slate-500">{p.industry || "—"} · {new Date(p.created_at).toLocaleDateString()}</div>
                </div>
                <Badge variant={p.status === "ready" ? "default" : p.status === "failed" ? "destructive" : "outline"}>
                  {p.status}
                </Badge>
              </button>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}


/**
 * Split into its own subcomponent so the wizard body stays readable.
 * Shows generation status, then a review pane with the AI content + a
 * one-click regenerate-creative button, plus download / email.
 */
function ProposalStatusView({ propId, status, initialProposal, onDone, onReset }) {
  // Show the POST response immediately so the running-state card is on-screen
  // without waiting for the first GET to complete.
  const [proposal, setProposal] = useState(initialProposal || null);
  const [regenerating, setRegenerating] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailPanel, setEmailPanel] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailName, setEmailName] = useState("");

  useEffect(() => {
    if (!propId) return;
    axios.get(`${API}/prospect-proposals/${propId}`).then(r => {
      setProposal(r.data);
      if (r.data.status !== status) onDone(r.data);
    }).catch(() => { /* keep prior state */ });
  }, [propId, status, onDone]);

  const isReady = proposal?.status === "ready";
  const isFailed = proposal?.status === "failed";
  const ai = proposal?.ai_content;

  const regenerateCreative = async () => {
    setRegenerating(true);
    try {
      const r = await axios.post(`${API}/prospect-proposals/${propId}/regenerate-creative`);
      setProposal(r.data);
      toast.success("New sample creative generated!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Regeneration failed");
    } finally {
      setRegenerating(false);
    }
  };

  const download = async (fmt) => {
    try {
      // Use fetch so we can catch a 501 (PDF unsupported on prod) and toast
      // a friendly message instead of the browser opening a raw JSON error.
      const url = `${API}/prospect-proposals/${propId}/download?format=${fmt}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) {
        let msg = `Download failed (${res.status})`;
        try { msg = (await res.json())?.detail || msg; } catch { /* not JSON */ }
        toast.error(msg);
        return;
      }
      const blob = await res.blob();
      const filename = (() => {
        const cd = res.headers.get("content-disposition") || "";
        const m = /filename="?([^";]+)"?/i.exec(cd);
        return m ? m[1] : `Prospect_IQ_Proposal.${fmt}`;
      })();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(objUrl);
    } catch (err) {
      toast.error("Download failed — please try again");
    }
  };

  const sendEmail = async () => {
    if (!emailTo.trim() || !/.+@.+\..+/.test(emailTo.trim())) {
      toast.error("Please enter a valid email");
      return;
    }
    setEmailing(true);
    try {
      const r = await axios.post(`${API}/prospect-proposals/${propId}/email-link`, {
        to_email: emailTo.trim(),
        to_name: emailName.trim() || null,
      });
      if (r.data?.email_sent === false) {
        toast.warning("Send routed through test-mode — no email will arrive on preview.");
      } else {
        toast.success(`Share link emailed to ${emailTo.trim()}`);
      }
      setEmailPanel(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally {
      setEmailing(false);
    }
  };

  const copyShareLink = async () => {
    try {
      const r = await axios.post(`${API}/prospect-proposals/${propId}/share`);
      const token = r.data?.share_token;
      if (!token) throw new Error("No token returned");
      const url = `${window.location.origin}/public/proposal/${token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Client link copied — paste into an email or message");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Copy failed");
    }
  };

  if (!proposal) return <Card><CardContent className="pt-6"><Loader2 className="h-6 w-6 animate-spin mx-auto text-slate-400" /></CardContent></Card>;

  return (
    <Card className="border-0 shadow-lg" data-testid="proposal-status-view">
      <CardHeader>
        <CardTitle className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-fuchsia-600" />
            {proposal.business_name}
          </div>
          <Badge variant={isReady ? "default" : isFailed ? "destructive" : "outline"} className={isReady ? "bg-emerald-600" : ""}>
            {isReady && <CheckCircle2 className="h-3 w-3 mr-1" />}
            {proposal.status}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isReady && !isFailed && (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-fuchsia-600 mb-3" />
            <p className="text-slate-700 font-medium">Generating your proposal…</p>
            <p className="text-xs text-slate-500 mt-1">Claude is writing demographics, triggers, and 3 sample ads. Nano Banana is rendering an FB ad creative.</p>
          </div>
        )}

        {isFailed && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800">
            <p className="font-semibold">Generation failed</p>
            <p className="text-xs mt-1">{proposal.error || "Unknown error"}</p>
            <Button variant="outline" onClick={onReset} className="mt-3" data-testid="proposal-restart-btn">Start over</Button>
          </div>
        )}

        {isReady && ai && (
          <>
            {/* PROMINENT FULL DECK BANNER — big buttons so reps understand
                they're getting a real presentation, not a one-sheet */}
            <div className="rounded-lg bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white p-4 sm:p-5" data-testid="full-deck-banner">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase text-fuchsia-100">Your Full Proposal Deck</div>
                  <div className="text-xl font-bold mt-0.5">{proposal.business_name}</div>
                  <div className="text-xs text-fuchsia-100 mt-0.5">Landscape 16:9 · matches the on-screen preview below</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => download("pptx")}
                    data-testid="download-pptx-btn"
                    className="bg-white text-fuchsia-700 hover:bg-fuchsia-50 font-semibold"
                  >
                    <Download className="h-4 w-4 mr-1" /> PPTX
                  </Button>
                  <Button
                    onClick={() => download("pdf")}
                    data-testid="download-pdf-btn"
                    variant="outline"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-semibold"
                  >
                    <Download className="h-4 w-4 mr-1" /> PDF
                  </Button>
                  <Button
                    onClick={copyShareLink}
                    data-testid="copy-share-link-btn"
                    variant="outline"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-semibold"
                  >
                    <LinkIcon className="h-4 w-4 mr-1" /> Copy Client Link
                  </Button>
                  <Button
                    onClick={() => setEmailPanel(p => !p)}
                    data-testid="email-toggle-btn"
                    variant="outline"
                    className="bg-white/10 border-white/30 text-white hover:bg-white/20 font-semibold"
                  >
                    <MailIcon className="h-4 w-4 mr-1" /> Email Link
                  </Button>
                </div>
              </div>
            </div>

            {/* Landscape slide-deck preview — actual PNG renders from the
                LibreOffice-converted PPTX. Rep clicks Present to go fullscreen
                for their Zoom call. */}
            <ProposalDeckViewer propId={propId} updatedAt={proposal.updated_at} />

            {/* Optional attachments — audit slides + homepage mockup PNG */}
            <AttachExtrasPanel
              proposal={proposal}
              onChanged={(fresh) => setProposal(fresh)}
            />

            {emailPanel && (
              <div className="rounded-lg border bg-slate-50 p-4 space-y-3" data-testid="email-panel">
                <div className="text-xs text-slate-600">
                  Sends a branded landing page link — client opens it and downloads the PPTX themselves. Reply-to is set to your email.
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label>Prospect email <span className="text-red-500">*</span></Label>
                    <Input type="email" data-testid="email-to-input" value={emailTo} onChange={e => setEmailTo(e.target.value)} placeholder="prospect@company.com" />
                  </div>
                  <div>
                    <Label>Their name (optional)</Label>
                    <Input data-testid="email-name-input" value={emailName} onChange={e => setEmailName(e.target.value)} placeholder="Jane Smith" />
                  </div>
                </div>
                <Button onClick={sendEmail} disabled={emailing} data-testid="email-send-btn" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white">
                  {emailing ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Sending…</>) : "Email proposal link"}
                </Button>
              </div>
            )}

            {/* AI content review — shown below the deck for reps who want to
                inspect what Claude wrote before presenting */}
            <Section icon={Users} title="Ideal Customer">
              <p className="text-sm text-slate-700">{ai.demographics?.ideal_customer_profile}</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
                <Stat label="Age" value={ai.demographics?.age_range} />
                <Stat label="Income" value={ai.demographics?.household_income} />
                <Stat label="Gender" value={ai.demographics?.gender_skew} />
                <Stat label="Homeowner" value={ai.demographics?.homeowner_share} />
              </div>
            </Section>

            {/* Triggers */}
            <Section icon={Sparkles} title="Purchase Triggers">
              {ai.triggers?.headline && <p className="text-sm font-semibold text-slate-900">{ai.triggers.headline}</p>}
              <ol className="mt-2 space-y-1 text-sm text-slate-700 list-decimal ml-5">
                {(ai.triggers?.reasons || []).map((r, i) => <li key={i}>{r}</li>)}
              </ol>
            </Section>

            {/* Creative Pack */}
            <Section icon={Wand2} title="Sample Creative" right={
              <Button size="sm" variant="outline" onClick={regenerateCreative} disabled={regenerating} data-testid="regenerate-creative-btn">
                {regenerating ? (<><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Regenerating…</>) : (<><Wand2 className="h-3 w-3 mr-1" /> Regenerate creative</>)}
              </Button>
            }>
              <div className="grid gap-3 md:grid-cols-3">
                <CreativeCard title="EMAIL" color="bg-slate-900">
                  <p className="text-xs font-semibold">Subject:</p>
                  <p className="text-sm text-slate-900 mb-2">{ai.creative?.email?.subject}</p>
                  <p className="text-xs text-slate-500 italic mb-2">{ai.creative?.email?.preview_text}</p>
                  <p className="text-xs text-slate-700 whitespace-pre-wrap">{ai.creative?.email?.body}</p>
                </CreativeCard>
                <CreativeCard title="META / FB AD" color="bg-orange-600">
                  {proposal.creative_image_asset_id && (
                    <img src={`${API_BASE}/api/mockup-assets/${proposal.creative_image_asset_id}`}
                         alt="Ad creative"
                         className="w-full rounded mb-2 object-cover aspect-square" />
                  )}
                  <p className="text-xs font-semibold">Headline:</p>
                  <p className="text-sm text-slate-900 mb-1">{ai.creative?.fb_ad?.headline}</p>
                  <p className="text-xs font-semibold">Primary:</p>
                  <p className="text-xs text-slate-700 mb-1">{ai.creative?.fb_ad?.primary_text}</p>
                  <p className="text-xs font-semibold">Description:</p>
                  <p className="text-xs text-slate-500">{ai.creative?.fb_ad?.description}</p>
                  {ai.creative?.fb_ad?.cta_button && (
                    <div className="inline-block mt-2 bg-orange-600 text-white text-xs font-bold px-3 py-1 rounded">{ai.creative.fb_ad.cta_button.toUpperCase()}</div>
                  )}
                </CreativeCard>
                <CreativeCard title="GOOGLE / SEM AD" color="bg-emerald-600">
                  <p className="text-xs text-slate-500 italic">Ad · {ai.creative?.google_ad?.final_url_hint}</p>
                  <p className="text-sm font-bold text-emerald-700 mb-1 mt-1">{(ai.creative?.google_ad?.headlines || [])[0]}</p>
                  <p className="text-xs text-slate-600 mb-2">{(ai.creative?.google_ad?.headlines || []).slice(1).filter(Boolean).join(" · ")}</p>
                  {(ai.creative?.google_ad?.descriptions || []).map((d, i) => (
                    <p key={i} className="text-xs text-slate-700 mb-1">{d}</p>
                  ))}
                </CreativeCard>
              </div>
              {ai.creative?.keywords?.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs font-bold tracking-widest uppercase text-fuchsia-700 mb-2">Starter Keywords</p>
                  <div className="flex flex-wrap gap-1">
                    {ai.creative.keywords.map((k, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{k}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* Pricing */}
            <Section icon={Wallet} title={`Investment · ${proposal.contract_months}-mo term`}>
              <div className="space-y-1 text-sm">
                {(proposal.line_items || []).map((it, i) => (
                  <div key={i} className="flex items-center justify-between border-b py-1">
                    <span>{it.product_name} <span className="text-slate-400">· {it.tier_name}</span></span>
                    <span className="font-mono">${(it.monthly || it.one_time).toLocaleString()}{it.monthly ? "/mo" : ""}</span>
                  </div>
                ))}
              </div>
            </Section>

            {/* Secondary actions row */}
            <div className="flex flex-wrap gap-2 pt-2">
              <Button onClick={onReset} variant="ghost" data-testid="start-over-btn" className="ml-auto text-slate-500">
                Start another proposal
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}


function Section({ icon: Icon, title, children, right }) {
  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-fuchsia-600" />
          <h3 className="font-semibold text-slate-900">{title}</h3>
        </div>
        {right}
      </div>
      <div>{children}</div>
    </div>
  );
}


function Stat({ label, value }) {
  return (
    <div className="bg-slate-50 rounded px-2 py-1.5">
      <div className="text-[10px] font-bold tracking-widest uppercase text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-900">{value || "—"}</div>
    </div>
  );
}


function CreativeCard({ title, color, children }) {
  return (
    <div className="rounded border bg-slate-50 overflow-hidden">
      <div className={`${color} text-white text-xs font-bold px-3 py-1.5 tracking-widest`}>{title}</div>
      <div className="p-3 space-y-1">{children}</div>
    </div>
  );
}


/**
 * AttachExtrasPanel — lets the rep bolt a saved SEO/AEO audit and/or a
 * homepage mockup into the assembled deck. The backend PATCH the current
 * proposal doc with `audit_id` / `mockup_id`; render pipeline picks them up
 * on the next preview / download.
 */
function AttachExtrasPanel({ proposal, onChanged }) {
  const [audits, setAudits] = useState([]);
  const [mockups, setMockups] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/audits`).then(r => setAudits(r.data || [])).catch(() => setAudits([]));
    axios.get(`${API}/mockups`).then(r => setMockups(r.data || [])).catch(() => setMockups([]));
  }, []);

  const patch = async (key, val) => {
    setSaving(true);
    try {
      const r = await axios.patch(`${API}/prospect-proposals/${proposal.id}`, { [key]: val || "" });
      onChanged(r.data);
      toast.success(val ? "Attached — deck will refresh" : "Detached");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg border bg-slate-50 p-4 space-y-3" data-testid="attach-extras-panel">
      <div className="flex items-center gap-2">
        <PresentIcon className="h-4 w-4 text-fuchsia-600" />
        <span className="font-semibold text-sm text-slate-900">Bolt on extras</span>
        <span className="text-xs text-slate-500">— they auto-render as slides inside the deck</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label className="text-xs uppercase text-slate-500">AEO / SEO audit</Label>
          <Select
            value={proposal.audit_id || "none"}
            onValueChange={(v) => patch("audit_id", v === "none" ? "" : v)}
            disabled={saving}
          >
            <SelectTrigger data-testid="attach-audit-select">
              <SelectValue placeholder="No audit attached" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— no audit —</SelectItem>
              {audits.map(a => (
                <SelectItem key={a.id} value={a.id}>
                  {(a.client_name || a.website_url || a.id).slice(0, 60)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1">
            Injects the 2-page report card right after the sample creative.
          </p>
        </div>
        <div>
          <Label className="text-xs uppercase text-slate-500">Homepage mockup</Label>
          <Select
            value={proposal.mockup_id || "none"}
            onValueChange={(v) => patch("mockup_id", v === "none" ? "" : v)}
            disabled={saving}
          >
            <SelectTrigger data-testid="attach-mockup-select">
              <SelectValue placeholder="No mockup attached" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— no mockup —</SelectItem>
              {mockups.map(m => (
                <SelectItem key={m.id} value={m.id}>
                  {(m.business_name || m.id).slice(0, 60)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1">
            Adds a full-bleed screenshot of your Homepage Mockup as a bonus slide.
          </p>
        </div>
      </div>
    </div>
  );
}

